import { useState, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Settings,
  Building2,
  User,
  FileText,
  Plug,
  Save,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  BarChart3,
  Landmark,
  Users,
  Share2,
  Mail,
  ShoppingBag,
  Link2,
  Shield,
  Apple,
  LogOut,
  Key,
  Brain,
} from "lucide-react";
import { GraftonBrain } from "@/components/GraftonBrain";
import { useNavigate } from "react-router-dom";

interface CompanySettings {
  company_name: string;
  siret: string;
  legal_form: string;
  address: string;
  city: string;
  postal_code: string;
  capital: string;
  creation_date: string;
  naf_code: string;
  employees_count: string;
  company_phone: string;
  company_email: string;
  website_url: string;
  director_first_name: string;
  director_last_name: string;
  director_email: string;
  director_phone: string;
  director_role: string;
  director_birth_date: string;
  director_nationality: string;
  kbis_url: string;
  statuts_url: string;
  rib_url: string;
  attestation_url: string;
  bilan_url: string;
  cv_url: string;
  id_card_url: string;
  api_connections: Record<string, unknown>;
}

const defaultSettings: CompanySettings = {
  company_name: "", siret: "", legal_form: "", address: "", city: "", postal_code: "",
  capital: "", creation_date: "", naf_code: "", employees_count: "", company_phone: "",
  company_email: "", website_url: "",
  director_first_name: "", director_last_name: "", director_email: "", director_phone: "",
  director_role: "", director_birth_date: "", director_nationality: "",
  kbis_url: "", statuts_url: "", rib_url: "", attestation_url: "", bilan_url: "",
  cv_url: "", id_card_url: "",
  api_connections: {},
};

const legalForms = [
  "Auto-entrepreneur", "EURL", "SARL", "SAS", "SASU", "SA", "SCI", "SNC", "Association",
];

const apiServices = [
  { id: "accounting", name: "Comptabilité", description: "Pennylane, QuickBooks, Sage", Icon: BarChart3, gradient: "from-blue-500 to-indigo-600" },
  { id: "banking", name: "Banque", description: "Qonto, Shine, Boursorama", Icon: Landmark, gradient: "from-emerald-500 to-teal-600" },
  { id: "crm", name: "CRM", description: "HubSpot, Salesforce, Pipedrive", Icon: Users, gradient: "from-orange-500 to-rose-500" },
  { id: "social", name: "Réseaux sociaux", description: "Meta, LinkedIn, X", Icon: Share2, gradient: "from-violet-500 to-purple-600" },
  { id: "email_marketing", name: "Email Marketing", description: "Mailchimp, Brevo, SendinBlue", Icon: Mail, gradient: "from-sky-500 to-cyan-600" },
  { id: "ecommerce", name: "E-commerce", description: "Shopify, WooCommerce, Stripe", Icon: ShoppingBag, gradient: "from-amber-500 to-yellow-600" },
  { id: "messagerie", name: "Messagerie", description: "Gmail, Outlook, Yahoo Mail", Icon: Mail, gradient: "from-rose-500 to-pink-600" },
];

const documentFields = [
  { key: "kbis_url", label: "Kbis", description: "Extrait Kbis de moins de 3 mois" },
  { key: "statuts_url", label: "Statuts", description: "Statuts constitutifs de la société" },
  { key: "rib_url", label: "RIB", description: "Relevé d'identité bancaire" },
  { key: "attestation_url", label: "Attestation URSSAF", description: "Attestation de vigilance" },
  { key: "bilan_url", label: "Bilan comptable", description: "Dernier bilan certifié" },
  { key: "cv_url", label: "CV du dirigeant", description: "Curriculum vitae à jour" },
  { key: "id_card_url", label: "Pièce d'identité", description: "CNI ou passeport en cours de validité" },
];

const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<CompanySettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [brainOpen, setBrainOpen] = useState(false);

  useEffect(() => {
    if (user) loadSettings();
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("company_settings")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    if (data) {
      const s: CompanySettings = { ...defaultSettings };
      for (const key of Object.keys(defaultSettings) as (keyof CompanySettings)[]) {
        if (key === "api_connections") {
          s.api_connections = (data as any).api_connections || {};
        } else {
          (s as any)[key] = (data as any)[key] || "";
        }
      }
      setSettings(s);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const payload = {
      ...settings,
      api_connections: settings.api_connections as any,
      user_id: user.id,
    };
    const { data: existing } = await supabase
      .from("company_settings")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    let error;
    if (existing) {
      ({ error } = await supabase
        .from("company_settings")
        .update(payload as any)
        .eq("user_id", user.id));
    } else {
      ({ error } = await supabase
        .from("company_settings")
        .insert(payload as any));
    }
    setSaving(false);
    if (error) {
      toast.error("Erreur lors de la sauvegarde");
      console.error(error);
    } else {
      toast.success("Paramètres sauvegardés avec succès");
    }
  };

  const handleFileUpload = async (docKey: string, file: File) => {
    if (!user) return;
    setUploadingDoc(docKey);
    const filePath = `${user.id}/${docKey}_${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from("company-documents")
      .upload(filePath, file);
    if (uploadError) {
      toast.error("Erreur lors du téléchargement");
      setUploadingDoc(null);
      return;
    }
    const { data: urlData } = supabase.storage
      .from("company-documents")
      .getPublicUrl(filePath);
    setSettings((prev) => ({ ...prev, [docKey]: urlData.publicUrl }));
    toast.success("Document téléchargé");
    setUploadingDoc(null);
  };

  const handleRemoveDoc = (docKey: string) => {
    setSettings((prev) => ({ ...prev, [docKey]: "" }));
  };

  const updateField = (key: keyof CompanySettings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const completionPercent = () => {
    const fields = Object.entries(settings).filter(([k]) => k !== "api_connections");
    const filled = fields.filter(([, v]) => typeof v === "string" && v.trim() !== "").length;
    return Math.round((filled / fields.length) * 100);
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <DashboardSidebar />
          <main className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/10">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.08),transparent_60%)]" />
            <div className="relative px-6 py-8 md:px-10 md:py-10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Settings className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                      Paramètres
                    </h1>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Centralisez les informations de votre entreprise pour automatiser vos démarches
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Profil complété</p>
                    <p className="text-lg font-bold text-primary">{completionPercent()}%</p>
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="gap-2" variant="hero">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Sauvegarder
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 md:px-10 py-6">
            <Tabs defaultValue="company" className="space-y-6">
              <div className="flex items-center gap-3">
                <TabsList className="grid w-full grid-cols-5 max-w-3xl">
                  <TabsTrigger value="company" className="gap-2 text-xs">
                    <Building2 className="h-3.5 w-3.5" /> Entreprise
                  </TabsTrigger>
                  <TabsTrigger value="director" className="gap-2 text-xs">
                    <User className="h-3.5 w-3.5" /> Dirigeant
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="gap-2 text-xs">
                    <FileText className="h-3.5 w-3.5" /> Documents
                  </TabsTrigger>
                  <TabsTrigger value="api" className="gap-2 text-xs">
                    <Plug className="h-3.5 w-3.5" /> Connexions
                  </TabsTrigger>
                  <TabsTrigger value="account" className="gap-2 text-xs">
                    <Shield className="h-3.5 w-3.5" /> Compte
                  </TabsTrigger>
                </TabsList>
                <Button
                  variant="outline"
                  onClick={() => setBrainOpen(true)}
                  className="gap-2 shrink-0 border-primary/30 hover:border-primary hover:bg-primary/5 transition-all"
                >
                  <Brain className="h-4 w-4 text-primary" />
                  <span className="hidden sm:inline text-xs">🧠 Cerveau</span>
                </Button>
              </div>

              <GraftonBrain open={brainOpen} onClose={() => setBrainOpen(false)} />

              {/* ENTREPRISE */}
              <TabsContent value="company">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      Informations de l'entreprise
                    </CardTitle>
                    <CardDescription>
                      Ces données seront utilisées pour pré-remplir automatiquement vos dossiers de financement
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Raison sociale</Label>
                      <Input value={settings.company_name} onChange={(e) => updateField("company_name", e.target.value)} placeholder="Ma Société SAS" />
                    </div>
                    <div className="space-y-2">
                      <Label>SIRET</Label>
                      <Input value={settings.siret} onChange={(e) => updateField("siret", e.target.value)} placeholder="123 456 789 00012" />
                    </div>
                    <div className="space-y-2">
                      <Label>Forme juridique</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={settings.legal_form}
                        onChange={(e) => updateField("legal_form", e.target.value)}
                      >
                        <option value="">Sélectionner...</option>
                        {legalForms.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Code NAF</Label>
                      <Input value={settings.naf_code} onChange={(e) => updateField("naf_code", e.target.value)} placeholder="6201Z" />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>Adresse</Label>
                      <Input value={settings.address} onChange={(e) => updateField("address", e.target.value)} placeholder="12 rue de la Paix" />
                    </div>
                    <div className="space-y-2">
                      <Label>Ville</Label>
                      <Input value={settings.city} onChange={(e) => updateField("city", e.target.value)} placeholder="Paris" />
                    </div>
                    <div className="space-y-2">
                      <Label>Code postal</Label>
                      <Input value={settings.postal_code} onChange={(e) => updateField("postal_code", e.target.value)} placeholder="75002" />
                    </div>
                    <div className="space-y-2">
                      <Label>Capital social</Label>
                      <Input value={settings.capital} onChange={(e) => updateField("capital", e.target.value)} placeholder="10 000 €" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de création</Label>
                      <Input type="date" value={settings.creation_date} onChange={(e) => updateField("creation_date", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Effectif</Label>
                      <Input value={settings.employees_count} onChange={(e) => updateField("employees_count", e.target.value)} placeholder="5 salariés" />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input value={settings.company_phone} onChange={(e) => updateField("company_phone", e.target.value)} placeholder="01 23 45 67 89" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={settings.company_email} onChange={(e) => updateField("company_email", e.target.value)} placeholder="contact@masociete.fr" />
                    </div>
                    <div className="space-y-2">
                      <Label>Site web</Label>
                      <Input value={settings.website_url} onChange={(e) => updateField("website_url", e.target.value)} placeholder="https://masociete.fr" />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* DIRIGEANT */}
              <TabsContent value="director">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="h-5 w-5 text-primary" />
                      Informations du dirigeant
                    </CardTitle>
                    <CardDescription>
                      Vos informations personnelles pour les formulaires CERFA et dossiers administratifs
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom</Label>
                      <Input value={settings.director_first_name} onChange={(e) => updateField("director_first_name", e.target.value)} placeholder="Jean" />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom</Label>
                      <Input value={settings.director_last_name} onChange={(e) => updateField("director_last_name", e.target.value)} placeholder="Dupont" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={settings.director_email} onChange={(e) => updateField("director_email", e.target.value)} placeholder="jean@masociete.fr" />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input value={settings.director_phone} onChange={(e) => updateField("director_phone", e.target.value)} placeholder="06 12 34 56 78" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fonction</Label>
                      <Input value={settings.director_role} onChange={(e) => updateField("director_role", e.target.value)} placeholder="Président / Gérant" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de naissance</Label>
                      <Input type="date" value={settings.director_birth_date} onChange={(e) => updateField("director_birth_date", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Nationalité</Label>
                      <Input value={settings.director_nationality} onChange={(e) => updateField("director_nationality", e.target.value)} placeholder="Française" />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* DOCUMENTS */}
              <TabsContent value="documents">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Documents clés
                    </CardTitle>
                    <CardDescription>
                      Téléchargez vos documents une seule fois — Grafton les réutilisera pour toutes vos démarches
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {documentFields.map((doc) => {
                      const hasFile = !!(settings as any)[doc.key];
                      return (
                        <div
                          key={doc.key}
                          className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30"
                        >
                          <div className="flex items-center gap-3">
                            {hasFile ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-foreground">{doc.label}</p>
                              <p className="text-xs text-muted-foreground">{doc.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {hasFile && (
                              <>
                                <Badge variant="secondary" className="text-[10px]">Téléchargé</Badge>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => handleRemoveDoc(doc.key)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                            <label>
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleFileUpload(doc.key, file);
                                }}
                              />
                              <Button
                                variant={hasFile ? "outline" : "default"}
                                size="sm"
                                className="gap-1.5 cursor-pointer"
                                asChild
                                disabled={uploadingDoc === doc.key}
                              >
                                <span>
                                  {uploadingDoc === doc.key ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Upload className="h-3.5 w-3.5" />
                                  )}
                                  {hasFile ? "Remplacer" : "Télécharger"}
                                </span>
                              </Button>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* API CONNECTIONS */}
              <TabsContent value="api">
                <Card>
                  <CardHeader>
                       <CardTitle className="text-lg flex items-center gap-2">
                      <Link2 className="h-5 w-5 text-primary" />
                      Connexions API
                    </CardTitle>
                    <CardDescription>
                      Connectez vos outils existants pour que Grafton synchronise automatiquement vos données
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {apiServices.map((service) => {
                      const isConnected = !!(settings.api_connections as any)?.[service.id];
                      return (
                        <div
                          key={service.id}
                          className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary/20 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg shadow-primary/5`}>
                              <service.Icon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{service.name}</p>
                              <p className="text-xs text-muted-foreground">{service.description}</p>
                            </div>
                          </div>
                          <Button
                            variant={isConnected ? "outline" : "default"}
                            size="sm"
                            className="gap-1.5"
                            onClick={() => {
                              toast.info("La connexion API sera disponible prochainement");
                            }}
                          >
                            {isConnected ? (
                              <>
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                Connecté
                              </>
                            ) : (
                              <>
                                <Plug className="h-3.5 w-3.5" />
                                Connecter
                              </>
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* COMPTE & SÉCURITÉ */}
              <TabsContent value="account">
                <div className="space-y-6">
                  {/* Méthode de connexion */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Key className="h-5 w-5 text-primary" />
                        Méthode de connexion
                      </CardTitle>
                      <CardDescription>
                        Visualisez comment vous êtes connecté et gérez votre authentification
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-secondary/30">
                        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          {(user as any)?.app_metadata?.provider === "apple" ? (
                            <Apple className="h-6 w-6 text-primary" />
                          ) : (
                            <Mail className="h-6 w-6 text-primary" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">
                            {(user as any)?.app_metadata?.provider === "apple"
                              ? "Connecté via Apple ID"
                              : (user as any)?.app_metadata?.provider === "google"
                              ? "Connecté via Google"
                              : "Connecté par email"}
                          </p>
                          <p className="text-xs text-muted-foreground">{user?.email}</p>
                        </div>
                        <Badge variant="outline" className="text-xs gap-1">
                          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          Actif
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Connexion Apple */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Apple className="h-5 w-5 text-primary" />
                        Apple Sign In
                      </CardTitle>
                      <CardDescription>
                        Connectez-vous rapidement et en toute sécurité avec votre Apple ID
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 rounded-xl border border-border bg-secondary/20 text-center">
                          <Shield className="h-6 w-6 text-primary mx-auto mb-2" />
                          <p className="text-xs font-medium">Sécurité maximale</p>
                          <p className="text-xs text-muted-foreground mt-1">Chiffrement Apple de bout en bout</p>
                        </div>
                        <div className="p-4 rounded-xl border border-border bg-secondary/20 text-center">
                          <Key className="h-6 w-6 text-primary mx-auto mb-2" />
                          <p className="text-xs font-medium">Sans mot de passe</p>
                          <p className="text-xs text-muted-foreground mt-1">Face ID, Touch ID ou code</p>
                        </div>
                        <div className="p-4 rounded-xl border border-border bg-secondary/20 text-center">
                          <User className="h-6 w-6 text-primary mx-auto mb-2" />
                          <p className="text-xs font-medium">Vie privée</p>
                          <p className="text-xs text-muted-foreground mt-1">Masquez votre email si souhaité</p>
                        </div>
                      </div>
                      {(user as any)?.app_metadata?.provider !== "apple" && (
                        <p className="text-xs text-muted-foreground bg-secondary/30 rounded-lg p-3">
                          💡 Pour utiliser Apple Sign In, déconnectez-vous puis reconnectez-vous en utilisant le bouton "Continuer avec Apple" sur la page de connexion.
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Sécurité */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5 text-primary" />
                        Sécurité du compte
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button
                        variant="outline"
                        className="w-full justify-start gap-2"
                        onClick={async () => {
                          const { error } = await supabase.auth.resetPasswordForEmail(user?.email || "", {
                            redirectTo: `${window.location.origin}/reset-password`,
                          });
                          if (error) toast.error("Erreur lors de l'envoi");
                          else toast.success("Email de réinitialisation envoyé");
                        }}
                      >
                        <Key className="h-4 w-4" />
                        Changer le mot de passe
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full justify-start gap-2 text-destructive hover:text-destructive"
                        onClick={async () => {
                          await supabase.auth.signOut();
                          window.location.href = "/auth";
                        }}
                      >
                        <LogOut className="h-4 w-4" />
                        Se déconnecter
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default SettingsPage;
